/**
 * useFormByParentPath
 *
 * Resolves the form that owns an ArTable from the table's `parentPath`, which is
 * not necessarily the routed form. Consumers are `ArTableControls` and
 * `ArWorkflowMenu`.
 *
 * Returns leaves, never the `Form` document. Handing back `snap.data` looks
 * narrow but is the opposite: proxy-compare records nothing for a container the
 * caller never reads a key on, and `isChanged` treats an unrecorded object as
 * changed — so a consumer that only forwards the document re-renders on every
 * write to it. The version document is subscribed one level down, inside
 * `ParentFormWorkflowParameterSidebar`, for the same reason (SYD-2264).
 */

import { useEffect, useMemo } from 'react';
import { useSnapshot } from 'valtio';
import type { Form } from '#types/System.types';
import { createDocumentStore } from '../../store';
import { parseFormVersionParentPath } from './formVersionParentPath';

const inactiveFormStore = createDocumentStore<Form>('forms/__inactive__');

export interface UseFormByParentPathResult {
  formId: string | undefined;
  versionId: string | undefined;
  formType: Form['formType'] | undefined;
  formNumber: Form['formNumber'] | undefined;
}

export function useFormByParentPath(parentPath: string): UseFormByParentPathResult {
  const parsed = useMemo(() => parseFormVersionParentPath(parentPath), [parentPath]);
  const formId = parsed?.formId;
  const versionId = parsed?.versionId;

  const formStore = useMemo(
    () => formId ? createDocumentStore<Form>(`forms/${formId}`) : inactiveFormStore,
    [formId],
  );

  useEffect(() => {
    if (!formId) return;
    formStore.connect();
    return () => formStore.disconnect();
  }, [formId, formStore]);

  const formSnap = useSnapshot(formStore.state);
  const formType = formSnap.data?.formType;
  const formNumber = formSnap.data?.formNumber;

  return {
    formId,
    versionId,
    formType: formId ? formType : undefined,
    formNumber: formId ? formNumber : undefined,
  };
}
